import { type ReactNode, useEffect } from 'react';

import { ApiActionStatus } from '@/interfaces';
import { SearchResultsItem } from '../../components/searchResultsItem/SearchResultsItem';
import { listOfSkeletons } from './components/skeletonHotel/SkeletonHotel';
import { LoadingItem } from '@/components/loadingItem/LoadingItem';
import SearchResultsEmptyWarning from './containers/searchResultsEmptyWarning/SearchResultsEmptyWarning';
import { fetchDirectPrices } from '@/state/directPricesActions';
import { hasDirectMapping } from '@/utils/integration/providers';
import { actions as mapActions } from '@/state/mapActions';
import { useUnleash } from '@/hooks/unleash';
import { VacationRentalWidget } from '../vacationRentalWidget/VacationRentalWidget';
import NotLoggedInWarning from './components/notLoggedInWarning/NotLoggedInWarning';
import useAppSelector from '@/hooks/useAppSelector';
import useAppDispatch from '@/hooks/useAppDispatch';
import { SortDetailsLink } from '@/scenes/searchResultsPage/components/sortDetailsLink/SortDetailsLink';
import { insertBanners, useAdPositionElements } from '@schibsted-nmp/advertising-companion';

interface Props {
    className?: string;
    openEditPanelWithFocus?: () => void;
}

export const SearchResults = ({ className = '', openEditPanelWithFocus }: Props) => {
    const dispatch = useAppDispatch();
    const results = useAppSelector((state) => state.searchResults.results);
    const providers = useAppSelector((state) => state.searchResults.providers);
    const destination = useAppSelector((state) => state.searchResults.destination);
    const status = useAppSelector((state) => state.searchResults.status);
    const isComplete = useAppSelector((state) => state.searchResults.isComplete);
    const totalFilteredResults = useAppSelector((state) => state.searchResults.totalFilteredResults);
    const searchConfig = useAppSelector((state) => state.searchResults.searchConfig);
    const filters = useAppSelector((state) => state.filters);
    const directPrices = useAppSelector((state) => state.directPrices);
    const mapOpen = useAppSelector((state) => state.map.open);
    const unleash = useUnleash();

    useEffect(() => {
        const hotelIds = results.map((hotel) => hotel.id);
        const pendingDirectIds = hotelIds.filter((hotelId) => hasDirectMapping(hotelId, unleash));

        if (
            pendingDirectIds.length > 0 &&
            filters.checkInDate &&
            filters.checkOutDate &&
            filters.rooms &&
            directPrices.status !== ApiActionStatus.PENDING
        ) {
            // @ts-expect-error ts doesn't like thunks being dispatched
            dispatch(fetchDirectPrices(pendingDirectIds, unleash.newAutocomplete));
        }
    }, [results, filters.checkInDate, filters.checkOutDate, filters.rooms]);

    const handleSetMapState = (payload: { selectedHotel: { key: string } }) => dispatch(mapActions.setMapState(payload));

    const isLoading = !isComplete || status === ApiActionStatus.PENDING;

    const getIsLoadingPrices = (hotelId: number) =>
        (isLoading || directPrices.status === ApiActionStatus.PENDING) &&
        ((hasDirectMapping(hotelId, unleash) && !directPrices.hotels[hotelId]) ||
            !directPrices.hotels[hotelId]?.some((offer) => offer.isComplete));

    const completedDirectPrices = (hotelId: number) => directPrices.hotels[hotelId]?.filter((offer) => offer.isComplete);

    const searchResultsNodeList: ReactNode[] = results.map(
        (hotel): ReactNode => (
            <SearchResultsItem
                providers={providers}
                filters={filters}
                directPriceIsLoading={getIsLoadingPrices(hotel.id)}
                directPriceList={completedDirectPrices(hotel.id)}
                destination={destination}
                setMapState={handleSetMapState}
                mapOpen={mapOpen}
                searchConfig={searchConfig}
                openEditPanelWithFocus={openEditPanelWithFocus}
                hotel={hotel}
                key={String(hotel.id)}
            />
        ),
    );

    const [bannerPositions, bannerElements] = useAdPositionElements({
        layoutType: 'list',
    });

    if (!isLoading && searchResultsNodeList.length > 0) {
        const widget = <VacationRentalWidget destination={destination} filters={filters} key="VacationRentalWidget" />;
        searchResultsNodeList.splice(5, 0, widget);
    }

    const adItemsWithBanners = insertBanners({
        adItems: searchResultsNodeList,
        bannerPositions,
        bannerElements,
    });

    return (
        <section id="searchResults" data-testid="searchResults" className={`u-position-relative ${className}`}>
            {isLoading && <LoadingItem />}
            <SortDetailsLink />
            {isLoading ? searchResultsNodeList.concat(listOfSkeletons(20 - searchResultsNodeList.length)).slice(0, 20) : adItemsWithBanners}
            {searchConfig?.requireLogin && <NotLoggedInWarning />}
            {isComplete && totalFilteredResults === 0 && <SearchResultsEmptyWarning />}
        </section>
    );
};

export default SearchResults;
